import { defineStore } from "pinia";
import { toast } from "@/utils/toast";
import savingsService from "@/services/hrm/savings-accounts.service";

const errMsg = (e) =>
  e?.response?.data?.message || e?.message || "Unexpected error";

export const useSavingsStore = defineStore("savings", {
  state: () => ({
    rows: [],
    total: 0,
    pageSize: 25,
    current: 1,
    loading: false,
    currentAccount: null,
    lastParams: {}, // ✅ remember last list params for refresh
  }),

  actions: {
    async fetch(params = {}) {
      this.loading = true;
      this.lastParams = { ...params };
      try {
        const { data } = await savingsService.list(params);

        // ✅ Supports either Laravel paginator OR plain array response
        if (Array.isArray(data)) {
          this.rows = data;
          this.total = data.length;
          this.pageSize = params.per_page || 25;
          this.current = params.page || 1;
        } else {
          this.rows = data.data || [];
          this.total = data.total || 0;
          this.pageSize = data.per_page || params.per_page || 25;
          this.current = data.current_page || params.page || 1;
        }

        return data;
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async refresh() {
      return this.fetch(this.lastParams || {});
    },

    async create(payload) {
      try {
        const { data } = await savingsService.create(payload);
        toast("Savings account created");
        // ✅ optional immediate refresh
        await this.refresh();
        return data;
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      }
    },

    async show(id) {
      try {
        const { data } = await savingsService.show(id);
        this.currentAccount = data;
        return data;
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      }
    },

    async update(id, payload) {
      try {
        const { data } = await savingsService.update(id, payload);
        toast("Savings account updated");
        await this.refresh();
        return data;
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      }
    },

    async destroy(id) {
      try {
        await savingsService.destroy(id);
        toast("Savings account deleted");
        await this.refresh();
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      }
    },
  },
});
